import { useState } from "react";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Popover,
  Button,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import _ from "lodash";

interface RoleItem {
  Name: string;
}

export const PopoverRoleList: React.FC<{
  onHide: () => void;
  roles: RoleItem[];
  onToggle: (role: RoleItem) => void;
  onClear?: () => void;
}> = ({ onHide, roles, onToggle, onClear }) => {
  return (
    <List
      dense
      subheader={<ListSubheader>Select Roles:</ListSubheader>}
      sx={{ minWidth: 200 }}
    >
      {roles &&
        roles.map((role, i: number) => (
          <ListItemButton key={i} onClick={() => onToggle(role)} divider>
            <ListItemText primary={role.Name} />
          </ListItemButton>
        ))}
      <ListItem>
        <Stack direction="row" spacing={1} sx={{ ml: "auto" }}>
          <Button size="small" startIcon={<DeleteIcon />} onClick={onClear}>
            Clear
          </Button>
          <Button size="small" startIcon={<CloseIcon />} onClick={onHide}>
            Close
          </Button>
        </Stack>
      </ListItem>
    </List>
  );
};

/**
 * Hook to select group roles
 */
// eslint-disable-next-line react-refresh/only-export-components -- hook is co-located with its popover component
export function useSelectRolePopover() {
  const dictionary: RoleItem[] = [
    { Name: "Admin" },
    { Name: "Member" },
    { Name: "Editor" },
    { Name: "Guest" },
    { Name: "None" },
  ];

  const [selectedRoles, setSelectedRoles] = useState<RoleItem[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_allRoles, setAllRoles] = useState(true);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const clearRoleFilter = (clear: boolean) => {
    setSelectedRoles([]);
    setAllRoles(clear);
  };

  const toggleRoleFilter = (checked: boolean, groupType: RoleItem) => {
    setAllRoles(false);
    let newSelection = [...selectedRoles];
    if (checked) {
      newSelection = _.union([groupType], newSelection);
    } else {
      newSelection = _.without(newSelection, groupType);
    }
    setSelectedRoles(newSelection);
  };

  const handleToggleRole = (groupType: RoleItem) => {
    toggleRoleFilter(true, groupType);
  };

  const removeRole = (groupType: RoleItem) => {
    toggleRoleFilter(false, groupType);
  };

  const presentRoles = (opts: { event: React.MouseEvent | Event }) => {
    const target = opts.event?.target || (opts.event as React.MouseEvent)?.currentTarget || null;
    setAnchorEl(target as HTMLElement | null);
  };

  const dismissRoles = () => {
    setAnchorEl(null);
  };

  const RolesPopover = (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={dismissRoles}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      sx={{ zIndex: 30000 }}
    >
      <PopoverRoleList
        onHide={dismissRoles}
        roles={dictionary}
        onToggle={handleToggleRole}
        onClear={() => clearRoleFilter(true)}
      />
    </Popover>
  );

  return {
    presentRoles,
    dismissRoles,
    clearRoleFilter,
    toggleRoleFilter,
    removeRole,
    handleToggleRole,
    selectedRoles,
    PopoverRoleList,
    roles: dictionary,
    RolesPopover,
  };
}
